import { createContext, useContext, useMemo, type ReactElement, type ReactNode } from 'react';
import type { AboutInfo } from '@ohpen/enso-ui';

import { useDevClient, useUserAccount, type DevClient } from '@pmp/components';
import { useAboutInfo } from '@pmp/hooks';

type UserAccount = ReturnType<typeof useUserAccount>;

/**
 * The chrome both shells put in the top bar. Held once for the session because the shells are
 * mounted at the same time: calling the hooks in each of them asked the agent for its version
 * twice per page, and left the previewed client in one of them frozen at mount time.
 */
export interface HostChrome {
  devClient: DevClient;
  aboutInfo: AboutInfo;
  userAccount: UserAccount;
}

const HostChromeContext = createContext<HostChrome | null>(null);

export const HostChromeProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const devClient = useDevClient();
  const aboutInfo = useAboutInfo();
  const userAccount = useUserAccount();

  const value = useMemo(() => ({ devClient, aboutInfo, userAccount }), [aboutInfo, devClient, userAccount]);

  return <HostChromeContext.Provider value={value}>{children}</HostChromeContext.Provider>;
};

export const useHostChrome = (): HostChrome => {
  const value = useContext(HostChromeContext);

  if (!value) {
    throw new Error('useHostChrome must be used inside a HostChromeProvider');
  }

  return value;
};
